import { useRef, useState } from "react";
import { ControllerRenderProps, FieldError } from "react-hook-form";
import { motion } from "framer-motion";
import {
  IoCloudUploadOutline,
  IoClose,
  IoDownloadOutline,
} from "react-icons/io5";
import {
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaFileImage,
  FaFileAlt,
} from "react-icons/fa";
import { useUploadFilesMutation } from "reduxFiles/api/fileApi";
import { API_URL } from "reduxFiles/api/baseApi";
import { useTranslation } from "react-i18next";
import IconButton from "components/button/IconButton";

interface FileMeta {
  originalName: string;
  size?: number;
  type?: string;
  id?: number;
  createdAt?: string;
}

interface Props {
  field: ControllerRenderProps<any, any>;
  label?: string;
  multiple?: boolean;
  maxFiles?: number;
  accept?: string;
  readOnly?: boolean;
  disabled?: boolean;
  showDownload?: boolean;
  required?: boolean;
  error?: FieldError;
}

export default function SmartUpload({
  field,
  label,
  multiple = false,
  maxFiles = 5,
  accept = "*/*",
  readOnly,
  disabled,
  showDownload = true,
  required,
  error,
}: Props) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploadFiles, { isLoading }] = useUploadFilesMutation();
  const [downloadLoading, setDownloadLoading] = useState<boolean>(false);

  const normalizeValue = (val: any): any[] => {
    if (!val) return [];

    if (Array.isArray(val)) {
      return val.map((item) => {
        if (typeof item === "string") {
          return { name: item };
        }
        if (item && typeof item === "object") {
          if (item.name) {
            return {
              name: item.name,
              originalName: item.originalName || item.name,
              size: item.size || 0,
              id: item.id,
              createdAt: item.createdAt,
              ...item,
            };
          }
          return item;
        }
        return item;
      });
    }

    if (typeof val === "string") {
      return [{ name: val }];
    }

    if (val && typeof val === "object" && val.name) {
      return [
        {
          name: val.name,
          originalName: val.originalName || val.name,
          size: val.size || 0,
          id: val.id,
          createdAt: val.createdAt,
          ...val,
        },
      ];
    }

    return [];
  };

  const value = normalizeValue(
    multiple ? field.value : field.value ? [field.value] : []
  );

  const getFileTypeFromName = (filename: string): string => {
    const ext = filename.split(".").pop()?.toLowerCase() || "";
    const mimeTypes: Record<string, string> = {
      pdf: "application/pdf",
      doc: "application/msword",
      docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      xls: "application/vnd.ms-excel",
      xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      jpg: "image/jpeg",
      jpeg: "image/jpeg",
      png: "image/png",
      gif: "image/gif",
      svg: "image/svg+xml",
      webp: "image/webp",
      mp4: "video/mp4",
      avi: "video/x-msvideo",
      mkv: "video/x-matroska",
      mov: "video/quicktime",
      mp3: "audio/mpeg",
      wav: "audio/wav",
      flac: "audio/flac",
      zip: "application/zip",
      rar: "application/x-rar-compressed",
      "7z": "application/x-7z-compressed",
      tar: "application/x-tar",
      gz: "application/gzip",
    };
    return mimeTypes[ext] || "application/octet-stream";
  };

  const [fileMeta, setFileMeta] = useState<Record<string, FileMeta>>(() => {
    const initialMeta: Record<string, FileMeta> = {};
    value.forEach((file) => {
      if (file?.name) {
        initialMeta[file.name] = {
          originalName: file.originalName || file.name,
          size: file.size || 0,
          id: file.id,
          createdAt: file.createdAt,
          type:
            file.type || getFileTypeFromName(file.originalName || file.name),
        };
      }
    });
    return initialMeta;
  });

  const canUploadMore = multiple ? value.length < maxFiles : value.length === 0;

  const handleUpload = async (fileList: FileList | null) => {
    if (!fileList?.length) return;

    const remainingSlots = maxFiles - value.length;
    const filesToUpload = Array.from(fileList).slice(0, remainingSlots);

    if (filesToUpload.length === 0) {
      alert(t("fileUpload.maxFilesReached", { max: maxFiles }));
      return;
    }

    if (filesToUpload.length < fileList.length) {
      alert(
        t("fileUpload.tooManyFiles", {
          remaining: remainingSlots,
          total: fileList.length,
        })
      );
    }

    try {
      const result = await uploadFiles(filesToUpload).unwrap();

      const metaUpdate: Record<string, FileMeta> = {};
      result.files.forEach((fileData) => {
        const originalFile = filesToUpload.find(
          (f) => f.name === fileData.originalName
        );
        metaUpdate[fileData.name] = {
          originalName: fileData.originalName,
          size: fileData.size,
          id: fileData.id,
          createdAt: fileData.createdAt,
          type:
            originalFile?.type || getFileTypeFromName(fileData.originalName),
        };
      });

      setFileMeta((prev) => ({
        ...prev,
        ...metaUpdate,
      }));

      if (!multiple) {
        field.onChange(result.files[0] || null);
      } else {
        const currentValue = normalizeValue(field.value);
        const newValue = [...currentValue, ...result.files];
        field.onChange(newValue);
      }
    } catch (error) {
      console.error("Upload error:", error);
      alert(t("fileUpload.uploadError"));
    }
  };

  const remove = (index: number) => {
    const currentValue = normalizeValue(field.value);

    if (!multiple) {
      field.onChange(null);
      const removedFile = currentValue[0];
      if (removedFile?.name) {
        setFileMeta((prev) => {
          const newMeta = { ...prev };
          delete newMeta[removedFile.name];
          return newMeta;
        });
      }
      return;
    }

    const updated = [...currentValue];
    const removedFile = updated[index];
    updated.splice(index, 1);

    if (removedFile?.name) {
      setFileMeta((prev) => {
        const newMeta = { ...prev };
        delete newMeta[removedFile.name];
        return newMeta;
      });
    }

    field.onChange(updated);
  };

  const handleDownload = async (filename: string) => {
    setDownloadLoading(true);

    try {
      const token = sessionStorage.getItem("accessToken");
      const response = await fetch(`${API_URL}/file/${filename}`, {
        method: "GET",
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
        },
      });

      if (!response.ok) {
        throw new Error("Download failed");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const originalName = fileMeta[filename]?.originalName || filename;
      link.download = originalName;

      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error("Download error:", error);
      alert(t("fileUpload.downloadError"));
    } finally {
      setDownloadLoading(false);
    }
  };

  const getFileIcon = (file: any) => {
    const filename = typeof file === "string" ? file : file?.originalName || "";
    const meta = fileMeta[filename];
    const nameForExt = meta?.originalName || filename;
    const ext = nameForExt.split(".").pop()?.toLowerCase() || "";

    const iconSize = 28;
    const iconClass = "flex-shrink-0";

    switch (ext) {
      case "pdf":
        return (
          <FaFilePdf size={iconSize} className={`${iconClass} text-red-600`} />
        );
      case "doc":
      case "docx":
        return (
          <FaFileWord
            size={iconSize}
            className={`${iconClass} text-blue-600`}
          />
        );
      case "xls":
      case "xlsx":
        return (
          <FaFileExcel
            size={iconSize}
            className={`${iconClass} text-green-700`}
          />
        );
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
      case "svg":
      case "webp":
        return (
          <FaFileImage
            size={iconSize}
            className={`${iconClass} text-purple-600`}
          />
        );
      default:
        return (
          <FaFileAlt size={iconSize} className={`${iconClass} text-gray-500`} />
        );
    }
  };

  const getFileExtension = (file: any) => {
    const filename = typeof file === "string" ? file : file?.originalName || "";
    const meta = fileMeta[filename];
    const nameForExt = meta?.originalName || filename;
    const ext = nameForExt.split(".").pop()?.toLowerCase() || "";
    return ext.toUpperCase();
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return "";
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    if (bytes < 1024 * 1024 * 1024)
      return (bytes / (1024 * 1024)).toFixed(1) + " MB";
    return (bytes / (1024 * 1024 * 1024)).toFixed(1) + " GB";
  };

  const formatAcceptTypes = (accept: string) => {
    if (accept === "*/*") return t("fileUpload.allFormats");

    const types = accept.split(",").map((type) => {
      const trimmed = type.trim();
      if (trimmed.startsWith(".")) {
        return trimmed.toUpperCase();
      }
      if (trimmed.includes("/")) {
        const parts = trimmed.split("/");
        if (parts[1] === "*") return parts[0].toUpperCase();
        return parts[1].toUpperCase();
      }
      return trimmed.toUpperCase();
    });

    return types.join(" | ");
  };

  return (
    <div className="w-full space-y-3">
      {label && (
        <label className="text-sm font-semibold text-gray-700 dark:text-white">
          {label}
          {multiple && (
            <span className="mr-2 text-xs font-normal text-gray-500">
              ({t("fileUpload.maxFiles", { max: maxFiles })})
            </span>
          )}
        </label>
      )}

      {!readOnly && !disabled && (
        <div
          onClick={() => canUploadMore && inputRef.current?.click()}
          className={`
            relative flex min-h-[120px] w-full cursor-pointer
            items-center justify-center rounded-3xl
            border-2 border-dashed ${error && "border-red-500"} transition
            ${
              canUploadMore
                ? `border-gray-300 bg-gray-50 ${
                    error
                      ? "hover:border-red-500 dark:border-red-500"
                      : "hover:border-indigo-400 dark:border-white/10"
                  } dark:bg-gray-900`
                : "cursor-not-allowed border-gray-200 bg-gray-100 opacity-60 dark:border-white/5 dark:bg-gray-800"
            }
          `}
        >
          <input
            ref={inputRef}
            type="file"
            hidden
            multiple={multiple}
            disabled={disabled || !canUploadMore || isLoading}
            accept={accept}
            onChange={(e) => handleUpload(e.target.files)}
          />

          {value.length === 0 && (
            <motion.div className="p-4 text-center">
              <IoCloudUploadOutline
                size={32}
                className="mx-auto text-indigo-500"
              />
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                {t("fileUpload.clickOrDrag")}
              </p>
              {accept !== "*/*" && (
                <p className="mt-1 text-xs text-gray-400">
                  {t("fileUpload.supportedFormats")}:{" "}
                  {formatAcceptTypes(accept)}
                </p>
              )}
              {multiple && (
                <p className="mt-1 text-xs text-gray-400">
                  {t("fileUpload.maxFiles", { max: maxFiles })}
                </p>
              )}
            </motion.div>
          )}

          {value.length > 0 && (
            <div className="w-full space-y-2 p-4">
              {value.map((file, index) => {
                const filename =
                  typeof file === "string" ? file : file?.name || "";
                const meta = fileMeta[filename] || {
                  originalName: file?.originalName || filename,
                  size: file?.size || 0,
                  type: file?.type || getFileTypeFromName(filename),
                  id: file?.id,
                  createdAt: file?.createdAt,
                };

                return (
                  <motion.div
                    key={filename + index}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-800"
                  >
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                      <div className="flex-shrink-0">{getFileIcon(file)}</div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-gray-800 dark:text-gray-200">
                          {meta.originalName}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                          <span className="font-mono">
                            {getFileExtension(file)}
                          </span>
                          <span>•</span>
                          <span dir="ltr">{formatFileSize(meta.size)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-shrink-0 items-center gap-2">
                      {showDownload && (
                        <IconButton
                          handler={() => {
                            handleDownload(filename);
                          }}
                          loading={downloadLoading}
                          title={t("buttons.download")}
                          icon={<IoDownloadOutline size={18} />}
                          size="sm"
                          variant="info"
                        />
                      )}
                      {!readOnly && !disabled && !isLoading && (
                        <IconButton
                          handler={() => {
                            remove(index);
                          }}
                          title={t("buttons.delete")}
                          icon={<IoClose size={18} />}
                          size="sm"
                          variant="danger"
                        />
                      )}
                    </div>
                  </motion.div>
                );
              })}

              {multiple && canUploadMore && value.length > 0 && (
                <p className="mt-2 text-center text-xs text-gray-400">
                  {value.length} {t("fileUpload.of")} {maxFiles}{" "}
                  {t("fileUpload.files")} • {maxFiles - value.length}{" "}
                  {t("fileUpload.remaining")}
                </p>
              )}
            </div>
          )}

          {isLoading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center rounded-3xl backdrop-blur-sm">
              <div className="flex flex-col items-center gap-2">
                <div className="loader w-6 text-blue-700" />
                <span className="text-sm font-medium">
                  {t("fileUpload.uploading")}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {readOnly && value.length > 0 && (
        <div className="space-y-2">
          {value.map((file) => {
            const filename = typeof file === "string" ? file : file?.name || "";
            const meta = fileMeta[filename] || {
              originalName: file?.originalName || filename,
              size: file?.size || 0,
              type: file?.type || getFileTypeFromName(filename),
              id: file?.id,
              createdAt: file?.createdAt,
            };

            return (
              <div
                key={filename}
                className="flex items-center justify-between rounded-xl bg-gray-50 p-3 dark:bg-gray-800"
              >
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0">{getFileIcon(file)}</div>
                  <div>
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {meta.originalName}
                    </span>
                    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                      <span className="font-mono">
                        {getFileExtension(file)}
                      </span>
                      <span>•</span>
                      <span>{formatFileSize(meta.size)}</span>
                    </div>
                  </div>
                </div>
                {showDownload && (
                  <IconButton
                    handler={() => {
                      handleDownload(filename);
                    }}
                    loading={downloadLoading}
                    title={t("buttons.download")}
                    icon={<IoDownloadOutline size={18} />}
                    size="sm"
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
